"use client";

import dayjs from "dayjs";

const getSheetPortalTarget = () =>
  typeof document !== "undefined"
    ? document.querySelector(".keobongvip-root") || document.body
    : null;
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import ChevronDownSvg from "@/assets/images/lu-chevron-down.svg";
import CloseSheetSvg from "@/assets/images/lu-close-sheet.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";
const THU_VI = [
  "Chủ nhật",
  "Thứ hai",
  "Thứ ba",
  "Thứ tư",
  "Thứ năm",
  "Thứ sáu",
  "Thứ bảy",
];

const OptionLabel = ({ opt, inline }) =>
  inline ? (
    <span className="text-sm font-bold leading-6">
      {opt.label}
      {opt.type !== "live" && (
        <span className="ml-1 font-bold text-[var(--theme-text)]">({opt.dateLabel})</span>
      )}
    </span>
  ) : (
    <div className="flex flex-col items-center px-3 py-1">
      <span className="inline-block w-[66px] text-center leading-6 md:w-auto md:font-bold">
        {opt.label}
      </span>
      {opt.type !== "live" && (
        <>
          <span className="hidden text-xs font-normal leading-6 text-[var(--theme-text-muted)] md:block md:text-sm">
            {opt.dateLabel}
          </span>
          <span className="ml-1 font-normal leading-6 text-[var(--theme-text)] md:hidden">
            ({opt.dateLabel})
          </span>
        </>
      )}
    </div>
  );

const LuDateTabs = ({ selectedDate, onDateChange, compact }) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  const today = useMemo(() => dayjs().tz(TZ), []);
  const todayString = today.format("YYYY-MM-DD");

  const dateOptions = useMemo(() => {
    const options = [];

    options.push({
      value: "live",
      label: "Kèo trực tuyến",
      dateLabel: today.format("DD-MM"),
      type: "live",
    });

    options.push({
      value: todayString,
      label: "Hôm nay",
      dateLabel: today.format("DD-MM"),
      type: "today",
    });

    const tomorrow = today.clone().add(1, "day");
    options.push({
      value: tomorrow.format("YYYY-MM-DD"),
      label: "Ngày mai",
      dateLabel: tomorrow.format("DD-MM"),
      type: "tomorrow",
    });

    for (let i = 2; i <= 6; i++) {
      const d = today.clone().add(i, "day");
      options.push({
        value: d.format("YYYY-MM-DD"),
        label: THU_VI[d.day()],
        dateLabel: d.format("DD-MM"),
        type: "future",
      });
    }

    return options;
  }, [todayString]);

  const selectedOpt = useMemo(
    () => dateOptions.find((o) => o.value === selectedDate) || dateOptions[1],
    [dateOptions, selectedDate],
  );

  const handleSelect = useCallback(
    (value) => {
      onDateChange(value);
      setSheetOpen(false);
    },
    [onDateChange],
  );

  return (
    <div
      className={`flex w-full ${compact ? "h-full min-h-0 items-stretch" : "items-center mb-4 mt-2 md:mb-6 md:mt-4"}`}
    >
      {/* Mobile */}
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="field relative z-0 flex h-full min-h-10 w-full items-center justify-between rounded-lg border border-solid border-[var(--scale-border)] kbv-gs-scale-border bg-[var(--theme-foreground)] px-2 md:hidden"
        aria-haspopup="listbox"
        aria-expanded={sheetOpen}
      >
        <select
          readOnly
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0"
        />
        <label className="field__label_select flex-1 text-left text-[var(--theme-text)]">
          <OptionLabel opt={selectedOpt} inline />
        </label>
        <img
          src={ChevronDownSvg}
          alt=""
          className="h-4 w-4 shrink-0"
          aria-hidden
        />
      </button>

      {/* Desktop */}
      <div className="lu-date-tabs hidden min-h-14 min-w-0 flex-1 overflow-x-auto overflow-y-hidden md:block">
        <div className="flex h-full w-max min-w-full items-center gap-x-2">
          {dateOptions.map((opt) => {
            const isSelected = selectedDate === opt.value;
            const hasDate = opt.type !== "live";

            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onDateChange(opt.value)}
                className={`flex h-14 min-w-[90px] shrink-0 cursor-pointer flex-col items-center justify-center rounded-lg border border-solid px-3 py-1 text-sm leading-6 font-bold transition-colors
                  ${isSelected ? "kbv-gs-tab-active" : "border-[var(--theme-border)] bg-[var(--theme-foreground)] text-[var(--theme-text)] lu-date-tab lu-date-tab--hover"}`}
              >
                <span className="whitespace-nowrap text-center leading-6">
                  {opt.label}
                </span>
                {hasDate && (
                  <span className="block text-xs font-normal leading-6 text-[var(--theme-text)] md:text-sm">
                    {opt.dateLabel}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Option on mobile */}
      {sheetOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[999] bg-[#2f323580] transition-opacity duration-300"
              style={{
                visibility: "visible",
                opacity: 1,
                backdropFilter: "blur(8px)",
              }}
              aria-hidden
              onClick={() => setSheetOpen(false)}
            />
            <div
              className="fixed bottom-0 left-0 right-0 z-[1000] flex h-[50vh] min-h-[240px] max-h-[420px] w-full flex-col rounded-t-xl bg-[var(--theme-foreground)] shadow-[0_4px_6px_rgba(0,0,0,0.1)] md:left-1/2 md:h-[420px] md:min-h-[420px] md:max-h-[420px] md:w-[420px] md:-translate-x-1/2"
              role="listbox"
              aria-label="Chọn Ngày"
            >
              <div className="relative flex shrink-0 items-center justify-center border-b border-[var(--theme-border)] py-3 md:py-4">
                <div className="text-sm font-bold text-[var(--theme-text)]">
                  Chọn Ngày
                </div>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1.5 hover:bg-[var(--theme-accent)]"
                  aria-label="Đóng"
                >
                  <img src={CloseSheetSvg} alt="" className="h-6 w-6" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-10">
                {dateOptions.map((opt) => {
                  const isSelected = selectedDate === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(opt.value)}
                      className={`flex h-[44px] w-full shrink-0 items-center justify-center border-0 bg-transparent px-6 text-center md:text-left md:justify-start ${
                        isSelected ? "kbv-gs-priv2-bg" : ""
                      }`}
                    >
                      <span className="text-sm font-bold leading-6 text-[var(--theme-text)]">
                        {opt.label}
                        {opt.type !== "live" && (
                          <span className="ml-1 font-bold text-[var(--theme-text)]">
                            ({opt.dateLabel})
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>,
          getSheetPortalTarget(),
        )}
    </div>
  );
};

export default LuDateTabs;
